import { EsriPackages } from 'models/common-map-types';
import {
  ID_OUTBREAK_ICON_LAYER,
  ID_OUTBREAK_OUTLINE_LAYER,
  ID_OUTBREAK_RISK_LAYER,
} from 'utils/constants';
import mapHelper from 'utils/mapHelper';

import riskLayer from 'map/riskLayer';

import { GeoshapeVM2EsriGraphics } from './outbreakLayerHelpers';

const OUTBREAK_PRIMARY_COLOR = '#AE5451';
const OUTBREAK_HIGHLIGHT_COLOR = [154, 74, 72, 51];
const OUTBREAK_POLYGON_RED = [174, 84, 81];

const outbreakIconFeatureCollection = mapHelper.getLocationIconFeatureCollection({
    iconColor: OUTBREAK_PRIMARY_COLOR,
    fields: [
      {
        name: 'GEONAME_ID',
        alias: 'GEONAME_ID',
        type: 'esriFieldTypeInteger',
      },
      {
        name: 'REPORTED_CASES',
        alias: 'REPORTED_CASES',
        type: 'esriFieldTypeInteger',
      },
      {
        name: 'DEATHS',
        alias: 'DEATHS',
        type: 'esriFieldTypeInteger',
      },
      {
        name: 'LOCATION_NAME',
        alias: 'LOCATION_NAME',
        type: 'esriFieldTypeString',
      },
      {
        name: 'LOCATION_TYPE',
        alias: 'LOCATION_TYPE',
        type: 'esriFieldTypeString',
      },
    ],
  }),
  outbreakRiskFeatureCollection = riskLayer.createRiskFeatureCollection({
    color: OUTBREAK_HIGHLIGHT_COLOR,
    classBreakField: 'REPORTED_CASES',
    classBreakpoints: [
      // NOTE: 3bafeedf: size in esri is 3:4 to size in legend
      { max: 0, size: 0 },
      { min: 0, max: 10, size: 12 },
      { min: 11, max: 100, size: 18 },
      { min: 101, max: 1000, size: 24 },
      { min: 1001, max: 10000, size: 30 },
      { min: 10001, max: 100000, size: 36 },
      { min: 100000, max: Infinity, size: 42 },
    ],
    otherFields: [
      {
        name: 'GEONAME_ID',
        alias: 'GEONAME_ID',
        type: 'esriFieldTypeInteger',
      },
      {
        name: 'REPORTED_CASES',
        alias: 'REPORTED_CASES',
        type: 'esriFieldTypeInteger',
      },
      {
        name: 'DEATHS',
        alias: 'DEATHS',
        type: 'esriFieldTypeInteger',
      },
      {
        name: 'LOCATION_NAME',
        alias: 'LOCATION_NAME',
        type: 'esriFieldTypeString',
      },
      {
        name: 'LOCATION_TYPE',
        alias: 'LOCATION_TYPE',
        type: 'esriFieldTypeString',
      },
    ],
  }),
  outbreakLocationOutlineFeatureCollection = mapHelper.getPolygonFeatureCollection(
    [...OUTBREAK_POLYGON_RED, 255 * 0.051],
    OUTBREAK_POLYGON_RED
  );

export default class OutbreakLayer {
  tooltipsEnabled = true;
  _esriPackages: EsriPackages;
  _renderRequestTime;
  outbreakIconLayer;
  outbreakRiskLayer;
  outbreakOutlineLayer;
  map;
  _graphicsVal;

  constructor(esriPackages) {
    const { FeatureLayer } = esriPackages;
    this._esriPackages = esriPackages;
    this._renderRequestTime = null;

    // Layer showing the icons where the outbreak is located
    this.outbreakIconLayer = new FeatureLayer(outbreakIconFeatureCollection, {
      id: ID_OUTBREAK_ICON_LAYER,
      outFields: ['*'],
    });

    // Layer showing the risk to that outbreak using the size of the circle
    this.outbreakRiskLayer = new FeatureLayer(outbreakRiskFeatureCollection, {
      id: ID_OUTBREAK_RISK_LAYER,
      outFields: ['*'],
    });

    // Layer showing the country outline
    this.outbreakOutlineLayer = new FeatureLayer(outbreakLocationOutlineFeatureCollection, {
      id: ID_OUTBREAK_OUTLINE_LAYER,
      outFields: ['*'],
    });
  }

  initializeOnMap(map) {
    this.map = map;
    if (!map.getLayer(ID_OUTBREAK_OUTLINE_LAYER)) {
      map.addLayer(this.outbreakOutlineLayer);
    }
    if (!map.getLayer(ID_OUTBREAK_RISK_LAYER)) {
      map.addLayer(this.outbreakRiskLayer);
    }
    if (!map.getLayer(ID_OUTBREAK_ICON_LAYER)) {
      map.addLayer(this.outbreakIconLayer);
    }
  }

  // NOTE: shapes: GeoshapeVM[]
  addOutbreakGraphics(shapes) {
    const { outlineGraphics, riskGraphics, iconGraphics } = GeoshapeVM2EsriGraphics(
      this._esriPackages,
      shapes
    );

    this.outbreakOutlineLayer.applyEdits(outlineGraphics);
    this.outbreakRiskLayer.applyEdits(riskGraphics);
    this.outbreakIconLayer.applyEdits(iconGraphics);
  }

  setLayerOpacityOutline(opacity) {
    this.outbreakOutlineLayer.setOpacity(opacity);
    this.outbreakOutlineLayer.refresh();
  }

  setLayerOpacityRisks(opacity) {
    this.outbreakRiskLayer.setOpacity(opacity);
    this.outbreakRiskLayer.refresh();
  }

  setLayerOpacityIcons(opacity) {
    this.outbreakIconLayer.setOpacity(opacity);
    this.outbreakIconLayer.refresh();
  }

  setTooltipEnabled(flag) {
    this.tooltipsEnabled = flag;
  }

  clearOutbreakGraphics() {
    this.outbreakOutlineLayer.applyEdits(null, null, this.outbreakOutlineLayer.graphics || []);
    this.outbreakRiskLayer.applyEdits(null, null, this.outbreakRiskLayer.graphics || []);
    this.outbreakIconLayer.applyEdits(null, null, this.outbreakIconLayer.graphics || []);
  }

  showAll() {
    this.getMapLayerIds().forEach(id => this.map.getLayer(id).show());
  }

  hideAll() {
    this.getMapLayerIds().forEach(id => this.map.getLayer(id).hide());
  }

  setOutbreakIconOnHover(callback) {
    this.outbreakIconLayer.on('mouse-over', evt => this.tooltipsEnabled && callback(evt.graphic));
  }

  getMapLayerIds() {
    return [ID_OUTBREAK_OUTLINE_LAYER, ID_OUTBREAK_ICON_LAYER, ID_OUTBREAK_RISK_LAYER];
  }
}
